<script setup>

import { ref, computed, watch } from 'vue';

const props = defineProps({
    items: Array,
    //set the default color of the paginator
    class: {
        type: String,
        default: 'flex items-center justify-between border-t border-gray-200',
    },
});

const items = ref(props.items);

const maxItemsPerPage = ref(10);
const maxPages = ref(5);
const page = ref(1);
const previousButton = ref(null);
const nextButton = ref(null);

const emit = defineEmits(['pageChange']);

const currentPageItems = computed(() => {
    const start = (page.value - 1) * maxItemsPerPage.value;
    const end = start + maxItemsPerPage.value;

    return items.value.slice(start, end);
});

const totalPages = computed(() => {
    const pages = Math.ceil(items.value.length / maxItemsPerPage.value);

    if (page.value > pages) {
        page.value = pages;
    }

    return pages > 0 ? pages : 1;
});

const pagesToShow = computed(() => {
    const half = Math.floor(maxPages.value / 2);
    let start = page.value - half;
    let end = page.value + half;

    if (start <= 0) {
        start = 1;
        end = maxPages.value;
    }

    if (end > totalPages.value) {
        end = totalPages.value;
        start = end - maxPages.value + 1;
    }

    if (start <= 0) {
        start = 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => i + start);
});

const goToPage = (pageNumber) => {
    page.value = pageNumber;
    emit('pageChange', page.value);
};

const goToNextPage = () => {
    if (page.value < totalPages.value) {
        page.value++;
    }
    emit('pageChange', page.value);
};

const goToPreviousPage = () => {
    if (page.value > 1) {
        page.value--;
    }
    emit('pageChange', page.value);
};

const goToFirstPage = () => {
    page.value = 1;
    emit('pageChange', page.value);
};

const goToLastPage = () => {
    page.value = totalPages.value;
    emit('pageChange', page.value);
};

const setItems = (newItems) => {
    items.value = newItems;
    goToFirstPage();
};

const setMaxItemsPerPage = (event) => {
    let max = event.target.value;

    if (max === 'all') {
        max = items.value.length;
    } else {
        max = parseInt(max);
    }

    maxItemsPerPage.value = max;

    goToFirstPage();
};

defineExpose({
    currentPageItems,
    setItems,
    items
});

</script>

<template>
    <div>
        <nav :class="props.class" v-if="pagesToShow.length > 1">
            <div class="-mt-px flex w-0 flex-1">
                <button @click="goToPreviousPage" ref="previousButton" type="button"
                    class="inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium text-gray-500 hover:border-tan hover:text-tan group">
                    <font-awesome-icon :icon="['far', 'arrow-left']"
                        class="mr-3 h-5 w-5 text-gray-400 group-hover:text-tan" />
                    Vorige
                </button>
            </div>
            <div class="hidden md:-mt-px md:flex">
                <button v-if="totalPages > 5 && page > 3" @click="goToFirstPage" type="button"
                    class="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700">
                    1
                </button>
                <button v-for="pageNumber in pagesToShow" :key="pageNumber" @click="goToPage(pageNumber)" type="button"
                    :class="{ 'border-tan text-tan': pageNumber === page, 'border-transparent text-gray-500 hover:border-tan hover:text-tan': pageNumber !== page }"
                    class="inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium" aria-current="page">
                    {{ pageNumber }}
                </button>
                <button v-if="totalPages > 5 && page < totalPages - 2" @click="goToLastPage" type="button"
                    class="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700">
                    {{ totalPages }}
                </button>
            </div>
            <div
                class="md:hidden -mt-px inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium ">
                Pagina:
                <select v-model="page" @change="goToPage($event.target.value)"
                    class="block w-20 focus:outline-none focus:ring-0 pt-0 pb-0 text-center">
                    <option v-for="pageNumber in totalPages" :key="pageNumber" :value="pageNumber">{{ pageNumber }}
                    </option>
                </select>
            </div>
            <div class="-mt-px flex w-0 flex-1 justify-end">
                <button @click="goToNextPage" ref="nextButton" type="button"
                    class="inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium text-gray-500 hover:border-tan hover:text-tan group">
                    Volgende
                    <font-awesome-icon :icon="['far', 'arrow-right']"
                        class="ml-3 h-5 w-5 text-gray-400 group-hover:text-tan" />
                </button>
            </div>
        </nav>
        <div v-if="items.length > 10" class="hidden md:justify-end md:flex border-t-2 border-transparent pl-1 pt-4 text-sm font-medium">Toon
            <select v-model="maxItemsPerPage" @change="setMaxItemsPerPage"
                class="block w-20 text-base focus:outline-none focus:ring-0 sm:text-sm pt-0 pb-0">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">Alles</option>
            </select>
            van de {{ items.length }} resultaten
        </div>
    </div>
</template>